import React, { useMemo } from 'react';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { distance, polygonArea, polygonCentroid, formatLength, formatArea } from '../utils/geometry';

/**
 * MeasureOverlay3D - Zeigt Messpunkte, Linien, Maße und Flächen an
 *
 * Props:
 * - measurePoints: Array von {x, y} Positionen
 * - mousePosition: aktuelle Mausposition für Vorschau-Linie
 * - showArea: ob die Fläche angezeigt werden soll (wenn Polygon geschlossen)
 */
export function MeasureOverlay3D({
  measurePoints = [],
  mousePosition,
  showArea = true,
}) {
  // Fläche berechnen wenn mindestens 3 Punkte
  const areaInfo = useMemo(() => {
    if (measurePoints.length < 3 || !showArea) return null;

    const area = polygonArea(measurePoints);
    const centroid = polygonCentroid(measurePoints);

    return {
      area,
      areaM2: area / 1000000, // mm² zu m²
      centroid,
    };
  }, [measurePoints, showArea]);

  // Segment-Daten berechnen (Längen zwischen aufeinanderfolgenden Punkten)
  const segments = useMemo(() => {
    const segs = [];
    for (let i = 0; i < measurePoints.length - 1; i++) {
      const p1 = measurePoints[i];
      const p2 = measurePoints[i + 1];
      const dist = distance(p1, p2);
      const midpoint = {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2,
      };
      const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);

      segs.push({
        start: p1,
        end: p2,
        distance: dist,
        midpoint,
        angle,
        index: i + 1, // 1-basiert für Anzeige
      });
    }
    return segs;
  }, [measurePoints]);

  // Gesamtlänge
  const totalLength = useMemo(() => {
    return segments.reduce((sum, seg) => sum + seg.distance, 0);
  }, [segments]);

  // Polygon-Shape für Flächen-Füllung
  const areaShape = useMemo(() => {
    if (measurePoints.length < 3) return null;

    const shape = new THREE.Shape();
    shape.moveTo(measurePoints[0].x, measurePoints[0].y);
    for (let i = 1; i < measurePoints.length; i++) {
      shape.lineTo(measurePoints[i].x, measurePoints[i].y);
    }
    shape.closePath();
    return shape;
  }, [measurePoints]);

  if (measurePoints.length === 0) return null;

  return (
    <group>
      {/* Flächen-Füllung (wenn >= 3 Punkte) */}
      {areaShape && showArea && (
        <mesh position={[0, 0, 4]}>
          <shapeGeometry args={[areaShape]} />
          <meshBasicMaterial
            color="#22c55e"
            transparent
            opacity={0.15}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
      )}

      {/* Linien zwischen Messpunkten */}
      {segments.map((seg, i) => (
        <group key={`segment-${i}`}>
          {/* Linie */}
          <line>
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                count={2}
                array={new Float32Array([
                  seg.start.x, seg.start.y, 6,
                  seg.end.x, seg.end.y, 6
                ])}
                itemSize={3}
              />
            </bufferGeometry>
            <lineBasicMaterial color="#22c55e" linewidth={2} />
          </line>

          {/* Maß-Label */}
          <Html
            position={[seg.midpoint.x, seg.midpoint.y + 200, 15]}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            <div className="bg-green-600/90 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap font-mono">
              <span className="font-bold">{formatLength(seg.distance)} m</span>
            </div>
          </Html>
        </group>
      ))}

      {/* Vorschau-Linie zur Maus (wenn mindestens 1 Punkt) */}
      {measurePoints.length > 0 && mousePosition && (
        <group>
          <line>
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                count={2}
                array={new Float32Array([
                  measurePoints[measurePoints.length - 1].x,
                  measurePoints[measurePoints.length - 1].y,
                  6,
                  mousePosition.x,
                  mousePosition.y,
                  6
                ])}
                itemSize={3}
              />
            </bufferGeometry>
            <lineBasicMaterial color="#22c55e" linewidth={1} transparent opacity={0.5} />
          </line>

          {/* Vorschau-Maß */}
          {(() => {
            const lastPoint = measurePoints[measurePoints.length - 1];
            const previewDist = distance(lastPoint, mousePosition);
            const midX = (lastPoint.x + mousePosition.x) / 2;
            const midY = (lastPoint.y + mousePosition.y) / 2;

            return (
              <Html
                position={[midX, midY + 200, 15]}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                <div className="bg-green-500/70 text-white text-xs px-2 py-0.5 rounded shadow whitespace-nowrap font-mono">
                  {formatLength(previewDist)} m
                </div>
              </Html>
            );
          })()}
        </group>
      )}

      {/* Messpunkte */}
      {measurePoints.map((point, i) => (
        <group key={`measure-point-${i}`} position={[point.x, point.y, 7]}>
          {/* Punkt-Kreis */}
          <mesh>
            <circleGeometry args={[80, 16]} />
            <meshBasicMaterial color="#22c55e" />
          </mesh>

          {/* Punkt-Ring */}
          <mesh>
            <ringGeometry args={[80, 100, 16]} />
            <meshBasicMaterial color="#16a34a" />
          </mesh>

          {/* Punkt-Nummer */}
          <Html
            position={[120, 120, 0]}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            <div className="bg-gray-800/80 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold shadow">
              {i + 1}
            </div>
          </Html>
        </group>
      ))}

      {/* Flächen-Info (in der Mitte des Polygons) */}
      {areaInfo && areaInfo.area > 0 && (
        <Html
          position={[areaInfo.centroid.x, areaInfo.centroid.y, 20]}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          <div className="bg-green-700/90 text-white px-3 py-2 rounded-lg shadow-lg text-center">
            <div className="text-lg font-bold">
              {areaInfo.areaM2.toFixed(2)} m²
            </div>
            <div className="text-xs opacity-80">
              Umfang: {formatLength(totalLength)} m
            </div>
          </div>
        </Html>
      )}

      {/* Gesamt-Info (oben rechts vom ersten Punkt) */}
      {measurePoints.length > 1 && (
        <Html
          position={[measurePoints[0].x - 300, measurePoints[0].y + 400, 20]}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          <div className="bg-gray-900/90 text-white px-2 py-1 rounded shadow-lg text-xs">
            <div className="flex gap-2">
              <span className="text-gray-400">Punkte:</span>
              <span className="font-bold">{measurePoints.length}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-gray-400">Gesamt:</span>
              <span className="font-bold text-green-400">{formatLength(totalLength)} m</span>
            </div>
          </div>
        </Html>
      )}
    </group>
  );
}
